/**
 * Hooks para el Modulo de Gestion de Requisitos INDRA
 * PECAL 2210 / ISO 29148 / CMMI REQM
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import type {
  Requirement,
  RequirementLink,
  RequirementChange,
  RequirementFilters,
  RequirementFormData,
  RequirementLinkFormData,
  RequirementsStats,
  ComplianceFramework,
  ENSMeasure,
  RequirementBaseline,
  BaselineFormData,
  TraceabilityValidation,
  TraceabilityGraph,
  TraceabilityCoverageExtended,
  BaselineComparison,
} from '@/types/requirements';

const REQUIREMENTS_KEY = 'requirements';
const LINKS_KEY = 'requirement-links';
const CHANGES_KEY = 'requirement-changes';
const FRAMEWORKS_KEY = 'compliance-frameworks';
const ENS_KEY = 'ens-measures';
const STATS_KEY = 'requirements-stats';

// ==================== REQUISITOS ====================

export function useRequirements(filters?: RequirementFilters) {
  return useQuery({
    queryKey: [REQUIREMENTS_KEY, filters],
    queryFn: async () => {
      let query = api
        .from('requirements_with_links')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.search) {
        query = query.or(
          `title.ilike.%${filters.search}%,description.ilike.%${filters.search}%,requirement_id.ilike.%${filters.search}%`
        );
      }
      if (filters?.category && filters.category !== 'all') {
        query = query.eq('category', filters.category);
      }
      if (filters?.level && filters.level !== 'all') {
        query = query.eq('level', filters.level);
      }
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters?.priority && filters.priority !== 'all') {
        query = query.eq('priority', filters.priority);
      }
      if (filters?.verification_status && filters.verification_status !== 'all') {
        query = query.eq('verification_status', filters.verification_status);
      }
      if (filters?.assigned_to) {
        query = query.eq('assigned_to', filters.assigned_to);
      }
      if (filters?.compliance_standard) {
        query = query.contains('compliance_standards', [filters.compliance_standard]);
      }
      if (filters?.ens_measure) {
        query = query.contains('ens_measures', [filters.ens_measure]);
      }
      if (filters?.parent_id) {
        query = query.eq('parent_requirement_id', filters.parent_id);
      }
      if (filters?.created_from) {
        query = query.gte('created_at', filters.created_from);
      }
      if (filters?.created_to) {
        query = query.lte('created_at', filters.created_to);
      }

      const { data, error } = await query.limit(500);
      if (error) throw error;
      return (data || []) as Requirement[];
    },
  });
}

export function useRequirement(id: string | null) {
  return useQuery({
    queryKey: [REQUIREMENTS_KEY, 'detail', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api
        .from('requirements_with_links')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as Requirement;
    },
    enabled: !!id,
  });
}

export function useRequirementByCode(requirementId: string | null) {
  return useQuery({
    queryKey: [REQUIREMENTS_KEY, 'by-code', requirementId],
    queryFn: async () => {
      if (!requirementId) return null;
      const { data, error } = await api
        .from('requirements_with_links')
        .select('*')
        .eq('requirement_id', requirementId)
        .single();
      if (error) throw error;
      return data as Requirement;
    },
    enabled: !!requirementId,
  });
}

export function useChildRequirements(parentId: string | null) {
  return useQuery({
    queryKey: [REQUIREMENTS_KEY, 'children', parentId],
    queryFn: async () => {
      if (!parentId) return [];
      const { data, error } = await api
        .from('requirements_with_links')
        .select('*')
        .eq('parent_requirement_id', parentId)
        .order('requirement_id');
      if (error) throw error;
      return (data || []) as Requirement[];
    },
    enabled: !!parentId,
  });
}

export function useCreateRequirement() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (formData: RequirementFormData) => {
      const { data, error } = await api.rpc('create_requirement', {
        p_category: formData.category,
        p_level: formData.level,
        p_title: formData.title,
        p_description: formData.description,
        p_source_type: formData.source_type,
        p_priority: formData.priority,
        p_subcategory: formData.subcategory || null,
        p_rationale: formData.rationale || null,
        p_acceptance_criteria: formData.acceptance_criteria || null,
        p_source_reference: formData.source_reference || null,
        p_parent_requirement_id: formData.parent_requirement_id || null,
        p_verification_method: formData.verification_method || null,
        p_assigned_to: formData.assigned_to || null,
        p_responsible_team: formData.responsible_team || null,
        p_compliance_standards: formData.compliance_standards || [],
        p_ens_measures: formData.ens_measures || [],
        p_wbs_reference: formData.wbs_reference || null,
        p_estimated_effort_hours: formData.estimated_effort_hours || null,
        p_target_release: formData.target_release || null,
        p_created_by: user?.id || null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REQUIREMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_KEY] });
      toast({
        title: 'Requisito creado',
        description: 'El requisito se ha creado correctamente',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateRequirement() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Requirement> }) => {
      const { data, error } = await api
        .from('requirements')
        .update({
          ...updates,
          updated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as Requirement;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [REQUIREMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_KEY] });
      toast({
        title: 'Requisito actualizado',
        description: `${data.requirement_id} actualizado correctamente`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useApproveRequirement() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const { data, error } = await api.rpc('approve_requirement', {
        p_requirement_id: id,
        p_approver_id: user?.id,
        p_notes: notes || null,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REQUIREMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_KEY] });
      toast({
        title: 'Requisito aprobado',
        description: 'El requisito ha sido aprobado (CMMI REQM)',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error de aprobacion',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useChangeRequirementStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      reason,
    }: {
      id: string;
      status: Requirement['status'];
      reason?: string;
    }) => {
      const { data, error } = await api
        .from('requirements')
        .update({
          status,
          updated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as Requirement;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [REQUIREMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_KEY] });
      toast({
        title: 'Estado actualizado',
        description: `${data.requirement_id} ahora esta en estado: ${data.status}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteRequirement() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.from('requirements').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REQUIREMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_KEY] });
      toast({
        title: 'Requisito eliminado',
        description: 'El requisito ha sido eliminado',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ==================== ENLACES DE TRAZABILIDAD ====================

export function useRequirementLinks(requirementId: string | null) {
  return useQuery({
    queryKey: [LINKS_KEY, requirementId],
    queryFn: async () => {
      if (!requirementId) return [];
      const { data, error } = await api
        .from('requirement_links')
        .select('*')
        .eq('source_requirement_id', requirementId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as RequirementLink[];
    },
    enabled: !!requirementId,
  });
}

export function useIncomingLinks(requirementId: string | null) {
  return useQuery({
    queryKey: [LINKS_KEY, 'incoming', requirementId],
    queryFn: async () => {
      if (!requirementId) return [];
      const { data, error } = await api
        .from('requirement_links')
        .select('*')
        .eq('target_type', 'requirement')
        .eq('target_id', requirementId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as RequirementLink[];
    },
    enabled: !!requirementId,
  });
}

export function useCreateLink() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (formData: RequirementLinkFormData) => {
      const { data, error } = await api
        .from('requirement_links')
        .insert({
          ...formData,
          created_by: user?.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data as RequirementLink;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LINKS_KEY] });
      queryClient.invalidateQueries({ queryKey: [REQUIREMENTS_KEY] });
      toast({
        title: 'Enlace creado',
        description: 'La trazabilidad se ha registrado (PECAL 2210)',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteLink() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.from('requirement_links').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LINKS_KEY] });
      queryClient.invalidateQueries({ queryKey: [REQUIREMENTS_KEY] });
      toast({
        title: 'Enlace eliminado',
        description: 'El enlace de trazabilidad ha sido eliminado',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ==================== HISTORIAL DE CAMBIOS ====================

export function useRequirementHistory(requirementId: string | null) {
  return useQuery({
    queryKey: [CHANGES_KEY, requirementId],
    queryFn: async () => {
      if (!requirementId) return [];
      const { data, error } = await api
        .from('requirement_changes')
        .select(`
          *,
          profiles:changed_by(full_name)
        `)
        .eq('requirement_id', requirementId)
        .order('changed_at', { ascending: false });
      if (error) throw error;
      return (data || []).map((c: any) => ({
        ...c,
        changed_by_name: c.profiles?.full_name,
      })) as RequirementChange[];
    },
    enabled: !!requirementId,
  });
}

// ==================== ESTADISTICAS ====================

export function useRequirementsStats() {
  return useQuery({
    queryKey: [STATS_KEY],
    queryFn: async () => {
      const { data, error } = await api.rpc('get_requirements_stats');
      if (error) throw error;
      return data as RequirementsStats;
    },
    refetchInterval: 60000,
  });
}

export function useRequirementsCount() {
  return useQuery({
    queryKey: [REQUIREMENTS_KEY, 'count'],
    queryFn: async () => {
      const { count, error } = await api
        .from('requirements')
        .select('*', { count: 'exact', head: true });
      if (error) throw error;
      return count || 0;
    },
  });
}

// ==================== MARCOS NORMATIVOS ====================

export function useComplianceFrameworks() {
  return useQuery({
    queryKey: [FRAMEWORKS_KEY],
    queryFn: async () => {
      const { data, error } = await api
        .from('compliance_frameworks')
        .select('*')
        .order('code');
      if (error) throw error;
      return (data || []) as ComplianceFramework[];
    },
    staleTime: 1000 * 60 * 60, // 1 hora
  });
}

// ==================== MEDIDAS ENS ====================

export function useENSMeasures(filters?: { category?: string; level?: 'low' | 'medium' | 'high' }) {
  return useQuery({
    queryKey: [ENS_KEY, filters],
    queryFn: async () => {
      let query = api.from('ens_measures').select('*').order('measure_code');

      if (filters?.category) {
        query = query.eq('category', filters.category);
      }
      if (filters?.level === 'low') {
        query = query.eq('level_low', true);
      } else if (filters?.level === 'medium') {
        query = query.eq('level_medium', true);
      } else if (filters?.level === 'high') {
        query = query.eq('level_high', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as ENSMeasure[];
    },
    staleTime: 1000 * 60 * 60, // 1 hora
  });
}

export function useUpdateENSMeasure() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<ENSMeasure>;
    }) => {
      const { data, error } = await api
        .from('ens_measures')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as ENSMeasure;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [ENS_KEY] });
      toast({
        title: 'Medida ENS actualizada',
        description: `${data.measure_code}: ${data.name}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useENSStats() {
  return useQuery({
    queryKey: [ENS_KEY, 'stats'],
    queryFn: async () => {
      const { data, error } = await api.from('ens_measures').select('*');
      if (error) throw error;

      const measures = data || [];
      const total = measures.length;
      const implemented = measures.filter((m: ENSMeasure) => m.is_implemented).length;
      const inProgress = measures.filter(
        (m: ENSMeasure) => m.implementation_status === 'in_progress'
      ).length;
      const verified = measures.filter(
        (m: ENSMeasure) => m.implementation_status === 'verified'
      ).length;

      return {
        total,
        implemented,
        in_progress: inProgress,
        verified,
        not_started: total - implemented - inProgress,
        coverage_percentage: Math.round((implemented / total) * 100),
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
  });
}

// ==================== FASE 2: LINEAS BASE ====================

const BASELINES_KEY = 'requirement-baselines';
const TRACEABILITY_KEY = 'traceability';

export function useBaselines(filters?: { status?: string; type?: string }) {
  return useQuery({
    queryKey: [BASELINES_KEY, filters],
    queryFn: async () => {
      let query = api
        .from('baselines_with_stats')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters?.type && filters.type !== 'all') {
        query = query.eq('baseline_type', filters.type);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as RequirementBaseline[];
    },
  });
}

export function useBaseline(id: string | null) {
  return useQuery({
    queryKey: [BASELINES_KEY, 'detail', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api
        .from('baselines_with_stats')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as RequirementBaseline;
    },
    enabled: !!id,
  });
}

export function useBaselineRequirements(baselineId: string | null) {
  return useQuery({
    queryKey: [BASELINES_KEY, 'requirements', baselineId],
    queryFn: async () => {
      if (!baselineId) return [];
      const { data, error } = await api
        .from('baseline_requirements')
        .select('*')
        .eq('baseline_id', baselineId)
        .order('requirement_code');
      if (error) throw error;
      return data || [];
    },
    enabled: !!baselineId,
  });
}

export function useCreateBaseline() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (formData: BaselineFormData) => {
      const { data, error } = await api.rpc('create_baseline', {
        p_name: formData.name,
        p_description: formData.description || null,
        p_type: formData.baseline_type,
        p_requirement_ids: formData.requirement_ids,
        p_created_by: user?.id || null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (baselineId) => {
      queryClient.invalidateQueries({ queryKey: [BASELINES_KEY] });
      toast({
        title: 'Linea base creada',
        description: 'La linea base se ha creado correctamente (PECAL 2110)',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useApproveBaseline() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const { data, error } = await api.rpc('approve_baseline', {
        p_baseline_id: id,
        p_approver_id: user?.id,
        p_notes: notes || null,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASELINES_KEY] });
      toast({
        title: 'Linea base aprobada',
        description: 'La linea base ha sido congelada (PECAL 2110)',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error de aprobacion',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateBaselineStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { data, error } = await api
        .from('requirement_baselines')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASELINES_KEY] });
      toast({
        title: 'Estado actualizado',
        description: 'El estado de la linea base se ha actualizado',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteBaseline() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.from('requirement_baselines').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASELINES_KEY] });
      toast({
        title: 'Linea base eliminada',
        description: 'La linea base ha sido eliminada',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ==================== FASE 2: TRAZABILIDAD ====================

export function useTraceabilityValidation(requirementId: string | null) {
  return useQuery({
    queryKey: [TRACEABILITY_KEY, 'validation', requirementId],
    queryFn: async () => {
      if (!requirementId) return null;
      const { data, error } = await api.rpc('validate_requirement_traceability', {
        p_requirement_id: requirementId,
      });
      if (error) throw error;
      return (data?.[0] || null) as TraceabilityValidation | null;
    },
    enabled: !!requirementId,
  });
}

export function useTraceabilityGraph(rootRequirementId?: string | null, depth: number = 3) {
  return useQuery({
    queryKey: [TRACEABILITY_KEY, 'graph', rootRequirementId, depth],
    queryFn: async () => {
      const { data, error } = await api.rpc('get_traceability_graph', {
        p_root_requirement_id: rootRequirementId || null,
        p_depth: depth,
      });
      if (error) throw error;
      const result = data?.[0];
      return {
        nodes: result?.nodes || [],
        edges: result?.edges || [],
      } as TraceabilityGraph;
    },
  });
}

export function useTraceabilityCoverage() {
  return useQuery({
    queryKey: [TRACEABILITY_KEY, 'coverage'],
    queryFn: async () => {
      const { data, error } = await api.rpc('get_traceability_coverage');
      if (error) throw error;
      return (data?.[0] || null) as TraceabilityCoverageExtended | null;
    },
    refetchInterval: 60000,
  });
}

export function useCompareBaselines(baselineAId: string | null, baselineBId: string | null) {
  return useQuery({
    queryKey: [BASELINES_KEY, 'compare', baselineAId, baselineBId],
    queryFn: async () => {
      if (!baselineAId || !baselineBId) return [];
      const { data, error } = await api.rpc('compare_baselines', {
        p_baseline_a: baselineAId,
        p_baseline_b: baselineBId,
      });
      if (error) throw error;
      return (data || []) as BaselineComparison[];
    },
    enabled: !!baselineAId && !!baselineBId,
  });
}
